import { useTheme } from "@mui/material/styles";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useMemo, useRef } from "react";
import { TDR_Default } from "../../results/GraphControl";
import { useHighchartsTheme } from "./useHighchartsTheme";

// Load Highcharts modules
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/export-data")(Highcharts);
require("highcharts/modules/boost")(Highcharts);

interface Props {
  series: Partial<Highcharts.SeriesOptionsType>[];
  chartTitle?: string;
  chartSubtitle?: string;
  yAxisSettings?: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
  xAxisSettings?: Highcharts.XAxisOptions | Highcharts.XAxisOptions[];
  xAxisTitle?: string;
  xAxisType?: Highcharts.AxisTypeValue;
  enableLegend?: boolean;
  sharedTooltip?: boolean;
  tooltipOptions?: Highcharts.TooltipOptions;
  tdrId?: number;
  tdr?: [number, number, number];
  setTdr?: (value: [number, number, number]) => void;
  setTdrId?: (value?: number) => void;
  cableLengthUpdated?: boolean;
  setCableLengthUpdated?: (value: boolean) => void;
  resetPlotLines?: boolean;
  setResetPlotLines?: (value: boolean) => void;
  reflow?: number;
}

const exportMenuItems = ["downloadPNG", "downloadCSV"];
const lineWidth = 2;

export default function LineChart(props: Props) {
  const theme = useHighchartsTheme();
  const eaTheme = useTheme();
  Highcharts.setOptions(theme);

  const ref = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    ref.current?.chart.reflow();
  }, [props.reflow]);

  const chartOptions = useMemo(() => {
    const tdrColours = [
      eaTheme.palette.lightBlue.main,
      eaTheme.palette.lightPink.main,
      eaTheme.palette.lightGreen.main,
    ];

    const handleChartClick = (axis: Highcharts.Axis, xValue: number) => {
      if (props.tdr == null || props.setTdr == null || props.setTdrId == null) {
        return;
      }
      let tdrId = props.tdrId;
      if (tdrId === undefined) {
        if (props.tdr[0] === TDR_Default) {
          tdrId = 0;
        } else if (props.tdr[1] === TDR_Default) {
          tdrId = 1;
        } else if (props.tdr[2] === TDR_Default) {
          tdrId = 2;
        } else {
          return;
        }
      }
      axis.removePlotLine(tdrId.toString());
      let newTdr: [number, number, number] = [...props.tdr];
      newTdr[tdrId] = xValue;
      props.setTdr(newTdr);
      axis.addPlotLine({
        value: xValue,
        width: lineWidth,
        color: tdrColours[tdrId],
        id: tdrId.toString(),
      });
      props.setTdrId(undefined);
    };

    if (ref && ref.current && props.tdr && props.cableLengthUpdated && props.setCableLengthUpdated) {
      props.setCableLengthUpdated(false);
      handleChartClick(ref.current.chart.xAxis[0], props.tdr[0]);
      handleChartClick(ref.current.chart.xAxis[0], props.tdr[2]);
    }

    if (props.resetPlotLines && ref && ref.current && props.setResetPlotLines) {
      ref.current.chart.xAxis[0].removePlotLine("0");
      ref.current.chart.xAxis[0].removePlotLine("1");
      ref.current.chart.xAxis[0].removePlotLine("2");
      props.setResetPlotLines(false);
    }

    return {
      chart: {
        type: "line",
        marginRight: Array.isArray(props.yAxisSettings) && props.yAxisSettings?.length > 1 ? 70 : undefined,
        events: {
          click: (event: Highcharts.ChartClickEventObject) => {
            if (!event.xAxis) return;
            const axis = event.xAxis[0].axis;
            const xValue = event.xAxis[0].value;
            handleChartClick(axis, xValue);
          },
          load: function (this: Highcharts.Chart, e: Event) {
            if (!props.tdr) return;

            this.xAxis[0].addPlotLine({
              value: props.tdr[0],
              width: lineWidth,
              color: tdrColours[0],
              id: "0",
            });
            this.xAxis[0].addPlotLine({
              value: props.tdr[1],
              width: lineWidth,
              color: tdrColours[1],
              id: "1",
            });
            this.xAxis[0].addPlotLine({
              value: props.tdr[2],
              width: lineWidth,
              color: tdrColours[2],
              id: "2",
            });
          },
        },
      },
      time: {
        useUTC: false,
      },
      title: {
        text: props.chartTitle,
      },
      subtitle: {
        text: props.chartSubtitle,
      },
      legend: {
        enabled: props.enableLegend,
      },
      tooltip: {
        shared: props.sharedTooltip,
        ...props.tooltipOptions,
      },
      xAxis: {
        type: props.xAxisType || "linear",
        title: {
          text: props.xAxisTitle,
        },
        ...props.xAxisSettings,
      },
      yAxis: props.yAxisSettings,
      plotOptions: {
        series: {
          lineWidth: 2,
          events: {
            click: (event: Highcharts.SeriesClickEventObject) => {
              const axis = event.point.series.xAxis;
              const xValue = event.point.x;
              handleChartClick(axis, xValue);
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: exportMenuItems,
          },
        },
      },
      series: props.series,
    };
  }, [eaTheme.palette.lightBlue.main, eaTheme.palette.lightGreen.main, eaTheme.palette.lightPink.main, props]);

  return (
    <HighchartsReact
      ref={ref}
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={{ style: { height: "100%", width: "100%" } }}
    />
  );
}
